/**
 * useToolActivity - Phase 8: UX Review (Story 8.1)
 * BMad Agent: James (Senior Developer)
 * 
 * React hook for tracking and displaying tool activity in the UI
 * Connects to WebSocket for real-time tool status updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ToolActivity, ToolActivityType } from '../../components/emma/ToolActivityIndicator';

interface UseToolActivityOptions {
  maxActivities?: number;  // Max activities to keep in history
  autoCleanupMs?: number;  // Auto-remove completed activities after this time
}

interface UseToolActivityReturn {
  activities: ToolActivity[];
  activeCount: number;
  isAnyActive: boolean;
  startActivity: (type: ToolActivityType, details?: string) => string;
  completeActivity: (id: string, sourcesChecked?: number) => void;
  failActivity: (id: string, error?: string) => void;
  clearCompleted: () => void;
}

/**
 * Maps backend tool names to UX-friendly activity types
 */
const BACKEND_TO_ACTIVITY_TYPE: Record<string, ToolActivityType> = {
  // Web research tools
  'web_research_tool': 'web_search',
  'web_research': 'web_search',
  'playwright': 'web_search',
  'firecrawl': 'web_search',
  'scrapy': 'web_search',
  
  // Verification
  'verify_claim': 'verification',
  'fact_check': 'verification',
  
  // Knowledge/RAG
  'knowledge_graph': 'knowledge_graph',
  'neo4j': 'knowledge_graph',
  'rag_service': 'rag_retrieval',
  'rag': 'rag_retrieval',
  'vector_store': 'rag_retrieval',
  'chromadb': 'rag_retrieval',
  
  // Reranking
  'reranker': 'reranking',
  'reranker_service': 'reranking',
  
  // Translation
  'translation': 'translation',
  'translation_service': 'translation',
  'deepl': 'translation',
  
  // Document generation
  'document_generator': 'document_generation',
  'document_processor': 'document_generation',
  'mcp_document': 'document_generation',
  
  // Analysis
  'trend_analysis': 'trend_analysis',
  'competitive_intel': 'competitive_intel',
  'market_intelligence': 'competitive_intel',
  
  // Quality
  'quality_check': 'quality_check',
  'rubric_evaluator': 'quality_check',
  
  // Learning
  'learning_loop': 'learning',
  'learning_loop_service': 'learning',
  
  // ML
  'ml_intent': 'ml_classification',
  'intent_detector': 'ml_classification',
};

/**
 * Hook for managing tool activity state
 */
export function useToolActivity(options: UseToolActivityOptions = {}): UseToolActivityReturn {
  const { maxActivities = 20, autoCleanupMs = 30000 } = options;
  
  const [activities, setActivities] = useState<ToolActivity[]>([]);
  const cleanupTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      cleanupTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);
  
  // Start a new activity
  const startActivity = useCallback((type: ToolActivityType, details?: string): string => {
    const id = `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newActivity: ToolActivity = {
      id,
      type,
      startTime: new Date(),
      status: 'active',
      details,
    };
    
    setActivities(prev => {
      const updated = [newActivity, ...prev];
      // Keep max activities
      return updated.slice(0, maxActivities);
    });
    
    return id;
  }, [maxActivities]);
  
  // Complete an activity
  const completeActivity = useCallback((id: string, sourcesChecked?: number) => {
    setActivities(prev => 
      prev.map(a => 
        a.id === id 
          ? { ...a, status: 'complete' as const, endTime: new Date(), sourcesChecked } 
          : a
      )
    );
    
    // Auto-cleanup after delay
    const timer = setTimeout(() => {
      setActivities(prev => prev.filter(a => a.id !== id));
      cleanupTimers.current.delete(id);
    }, autoCleanupMs);
    
    cleanupTimers.current.set(id, timer);
  }, [autoCleanupMs]);
  
  // Fail an activity
  const failActivity = useCallback((id: string, error?: string) => {
    setActivities(prev => 
      prev.map(a => 
        a.id === id 
          ? { ...a, status: 'error' as const, endTime: new Date(), details: error || a.details } 
          : a
      )
    );
    
    // Auto-cleanup after longer delay for errors
    const timer = setTimeout(() => {
      setActivities(prev => prev.filter(a => a.id !== id));
      cleanupTimers.current.delete(id);
    }, autoCleanupMs * 2);
    
    cleanupTimers.current.set(id, timer);
  }, [autoCleanupMs]);
  
  // Clear all completed activities
  const clearCompleted = useCallback(() => {
    setActivities(prev => prev.filter(a => a.status === 'active'));
    
    // Clear all timers
    cleanupTimers.current.forEach(timer => clearTimeout(timer));
    cleanupTimers.current.clear();
  }, []);
  
  const activeCount = activities.filter(a => a.status === 'active').length;
  const isAnyActive = activeCount > 0;
  
  return {
    activities,
    activeCount,
    isAnyActive,
    startActivity,
    completeActivity,
    failActivity,
    clearCompleted,
  };
}

/**
 * Helper to convert backend tool event to activity type
 */
export function backendToolToActivityType(toolName: string): ToolActivityType {
  const normalized = toolName.toLowerCase().replace(/[^a-z_]/g, '');
  return BACKEND_TO_ACTIVITY_TYPE[normalized] || 'ml_classification';
}

export default useToolActivity;
